// The sheets each report exports.
//
// Most reports are exported by walking their payload (see excelExport.js), which is correct
// for anything that is a list or a summary. The ones built by hand here are the ones whose
// rows are READ a particular way -- the payout sheet, the funds flow, the grouped collection
// report -- where an analyst wants one flat table with the grouping as columns, not a sheet
// per landlord. Each hand-built export is still the same figures the screen shows; it is the
// layout that is chosen, never the numbers.

import { sheetsFromPayload, safeFilename } from './excelExport';

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const day = (v) => (v ? String(v).slice(0, 10) : null);

// ---------------------------------------------------------------- landlord reports

function rentRollSheets(data) {
  const rows = (data.rentRoll || []).map((r) => ({
    property: r.propertyName,
    unit: r.unitNumber,
    unitType: r.unitType,
    monthlyRent: num(r.rentAmount),
    tenant: r.isVacant ? 'Vacant' : r.tenantName,
    phone: r.tenantPhone,
    email: r.tenantEmail,
    leaseStart: day(r.leaseStartDate),
    leaseEnd: day(r.leaseEndDate),
    nextDue: day(r.nextDueDate),
    paymentStatus: r.paymentStatus,
    balance: num(r.balance),
    lastPayment: day(r.lastPaymentDate),
    currentInvoice: r.currentInvoice?.invoiceNumber || null,
    invoiceDue: day(r.currentInvoice?.dueDate),
    invoiceBalance: num(r.currentInvoice?.balance)
  }));
  return [{ name: 'Rent Roll', rows }];
}

function ownerStatementSheets(data) {
  const s = data.summary || {};
  const summary = [
    { item: 'Period from', value: day(data.period?.startDate) },
    { item: 'Period to', value: day(data.period?.endDate) },
    { item: 'Opening balance', value: num(data.openingBalance) },
    { item: 'Total rent collected', value: num(s.totalRentCollected) },
    { item: 'Total expenses', value: num(s.totalExpenses) },
    { item: 'Management fees', value: num(s.managementFees) },
    { item: 'Transfers & adjustments', value: num(s.adjustments) },
    { item: 'Net earned', value: num(s.netEarned) },
    { item: 'Paid to landlord', value: num(s.paymentsMade) },
    { item: 'Net payout (amount payable)', value: num(s.netPayout) }
  ];

  const entries = (list, extra = () => ({})) => (list || []).map((e) => ({
    date: day(e.date),
    description: e.description,
    property: e.property && e.property !== 'N/A' ? e.property : null,
    unit: e.unit || null,
    ...extra(e),
    amount: num(e.amount)
  }));

  return [
    { name: 'Summary', rows: summary },
    { name: 'Income', rows: entries(data.income?.entries) },
    { name: 'Expenses', rows: entries(data.expenses?.entries, (e) => ({ vendor: e.vendor || null })) },
    { name: 'Management Fees', rows: entries(data.agencyDeductions?.entries) },
    { name: 'Adjustments', rows: entries(data.adjustments?.entries, (e) => ({ source: e.sourceType || null })) },
    { name: 'Payments Made', rows: entries(data.paymentsMade?.entries) },
    {
      name: 'Property Breakdown',
      rows: (data.propertyBreakdown || []).map((p) => ({
        property: p.propertyName,
        income: num(p.income),
        expenses: num(p.expenses),
        managementFees: num(p.managementFees),
        netAmount: num(p.netAmount)
      }))
    }
  ].filter((sheet) => sheet.rows.length);
}

function paymentReportTenantCell(r) {
  if (r.tenantLabel) return r.tenantLabel;
  if (r.isVacant) return 'VACANT';
  const name = r.tenantName || '';
  if (r.isFormerTenant) return `${name} (PAST TENANT)`;
  if (r.isNewTenant) return `NEW TENANT — ${name}`;
  if (r.advancedRent) return `${name} (ADVANCED)`;
  return name;
}

/**
 * One continuous sheet matching the printed Bagalu payout report:
 * SUIT | TENANT | RENT PAYABLE, units then TOTAL / LESS… / AMOUNT TO BE PAID.
 * Exports every row from the report payload (not a paginated screen slice).
 */
function paymentReportSheets(data) {
  const groups = data.propertyGroups?.length
    ? data.propertyGroups
    : [{ propertyName: null, rows: data.rows || [], collection: data.totalCollection }];
  const multiple = groups.length > 1;
  const rows = [];

  groups.forEach((g) => {
    if (multiple) {
      rows.push({
        suit: '',
        tenant: String(g.propertyName || 'Unassigned').toUpperCase(),
        rentPayable: null,
        _bold: true
      });
    }
    (g.rows || []).forEach((r) => {
      rows.push({
        suit: r.isFormerTenant ? '' : (r.unitNumber || ''),
        tenant: paymentReportTenantCell(r),
        rentPayable: num(r.collection ?? 0)
      });
    });
    if (multiple) {
      rows.push({
        suit: '',
        tenant: `${String(g.propertyName || 'Unassigned').toUpperCase()} TOTAL`,
        rentPayable: num(g.collection),
        _bold: true
      });
    }
  });

  rows.push({ suit: '', tenant: 'TOTAL', rentPayable: num(data.totalCollection), _bold: true });

  const commissionLabel = (data.commission?.rateLabel
    ? `LESS ${data.commission.rateLabel} COMMISSION`
    : (data.commission?.label || 'Less Commission').toUpperCase());
  rows.push({ suit: '', tenant: commissionLabel, rentPayable: num(data.commission?.amount || 0) });

  (data.deductions?.lines || []).forEach((l) => {
    const where = l.unitNumber ? `${l.unitNumber} - ` : '';
    const part = l.partial ? ` (PART OF ${num(l.expenseAmount)})` : '';
    rows.push({
      suit: '',
      tenant: `LESS ${where}${l.description}`.toUpperCase() + part,
      rentPayable: num(l.amount)
    });
  });

  (data.adjustments?.lines || []).forEach((l) => {
    rows.push({
      suit: '',
      tenant: `${l.amount < 0 ? 'ADD' : 'LESS'} ${l.description}`.toUpperCase(),
      rentPayable: num(Math.abs(l.amount))
    });
  });

  if (data.advances?.rent > 0) {
    rows.push({ suit: '', tenant: 'ADD ADVANCE ON UNPAID RENT', rentPayable: num(data.advances.rent) });
    if (data.advances.commission > 0) {
      rows.push({
        suit: '',
        tenant: 'LESS COMMISSION ON ADVANCE',
        rentPayable: num(data.advances.commission)
      });
    }
  }

  rows.push({
    suit: '',
    tenant: 'AMOUNT TO BE PAID',
    rentPayable: num(data.amountPayable),
    _bold: true
  });

  const sheets = [{
    name: 'Payment Report',
    title: data.heading || null,
    reportLayout: true,
    columns: [
      { key: 'suit', header: 'SUIT' },
      { key: 'tenant', header: 'TENANT' },
      { key: 'rentPayable', header: 'RENT PAYABLE', numFmt: '#,##0' }
    ],
    rows
  }];

  if (data.advances?.lines?.length) {
    sheets.push({
      name: 'Advances',
      rows: data.advances.lines.map((a) => ({
        property: a.propertyName,
        unit: a.unitNumber,
        tenant: a.tenantName,
        rent: num(a.rentAmount),
        commission: num(a.commissionAmount),
        landlordReceives: num(a.netAmount)
      }))
    });
  }
  return sheets;
}

function fundsFlowSheets(data) {
  const rows = (data.lines || []).map((l) => ({
    date: day(l.entryDate),
    type: l.kind,
    description: l.description,
    fromTo: l.counterparty || null,
    paidOut: l.debit ? num(l.debit) : null,
    credited: l.credit ? num(l.credit) : null,
    balance: num(l.balance),
    standing: l.reversedByEntryId ? 'Reversed' : (l.reversesEntryId ? 'Reversal' : null)
  }));
  const summary = [
    { item: 'Account', value: data.account ? `${data.account.code} ${data.account.name}` : null },
    { item: 'Period from', value: day(data.period?.startDate) },
    { item: 'Period to', value: day(data.period?.endDate) },
    { item: 'Opening balance', value: num(data.opening?.balance) },
    { item: 'Total paid out', value: num(data.totals?.debit) },
    { item: 'Total credited', value: num(data.totals?.credit) },
    { item: 'Closing balance (owed to landlord)', value: num(data.closing) }
  ];
  if (data.truncated) summary.push({ item: 'Note', value: 'More lines than fit in one view; narrow the dates to see all of them.' });
  return [{ name: 'Transactions', rows }, { name: 'Summary', rows: summary }];
}

export function landlordReportSheets(slug, data) {
  if (!data) return [];
  switch (slug) {
    case 'rent-roll': return rentRollSheets(data);
    case 'owner-statement': return ownerStatementSheets(data);
    case 'payment-report': return paymentReportSheets(data);
    case 'funds-flow': return fundsFlowSheets(data);
    default: return sheetsFromPayload(data);
  }
}

// ---------------------------------------------------------------- agency reports

function outstandingCollectionSheets(data) {
  const period = data.period?.label || 'Period';
  const rows = [];
  (data.landlords || []).forEach((l) => {
    (l.properties || []).forEach((p) => {
      (p.rows || []).forEach((r) => {
        rows.push({
          landlord: l.landlordName,
          property: p.propertyName,
          unit: r.unitNumber,
          customer: r.tenantName,
          status: r.isCurrentTenant === false ? 'Past tenant' : null,
          debt: num(r.debt),
          [period]: num(r.charge),
          total: num(r.total),
          payment: num(r.payment),
          advancePayment: num(r.advance),
          totalCollection: num(r.totalCollection),
          revenue: num(r.revenue),
          balance: num(r.balance)
        });
      });
    });
  });

  const totalsRow = (label, t) => ({
    group: label,
    debt: num(t?.debt), [period]: num(t?.charge), total: num(t?.total), payment: num(t?.payment),
    advancePayment: num(t?.advance), totalCollection: num(t?.totalCollection),
    revenue: num(t?.revenue), balance: num(t?.balance)
  });
  const totals = [];
  (data.landlords || []).forEach((l) => {
    (l.properties || []).forEach((p) => totals.push(totalsRow(`${l.landlordName} / ${p.propertyName}`, p.subtotal)));
    totals.push(totalsRow(`${l.landlordName} (total)`, l.subtotal));
  });
  totals.push(totalsRow('GRAND TOTAL (groups)', data.totals));
  if (data.reportTotal) totals.push(totalsRow('TOTAL (with other revenues)', data.reportTotal));

  const other = [
    { line: 'Commission (from groups above)', amount: num(data.otherRevenues?.commission) },
    ...(data.otherRevenues?.lines || []).map((l) => ({ line: l.label, amount: num(l.amount) })),
    { line: 'TOTAL REVENUE', amount: num(data.otherRevenues?.total) }
  ];

  return [
    { name: 'Collection', rows },
    { name: 'Totals', rows: totals },
    { name: 'Other Revenues', rows: other }
  ];
}

export function agencyReportSheets(slug, data) {
  if (!data) return [];
  switch (slug) {
    case 'outstanding-collection': return outstandingCollectionSheets(data);
    default: return sheetsFromPayload(data);
  }
}

/** The file a download is saved as: what report, whose, and for when. */
export function reportFilename(slug, { landlordName = null, startDate = null, endDate = null } = {}) {
  return safeFilename([slug, landlordName, startDate && endDate ? `${day(startDate)}-to-${day(endDate)}` : day(startDate)]);
}
